import React from "react";
import PageLayout from "../components/layout/PageLayout";
import PageHero from "../components/shared/PageHero";
import SectionHeading from "../components/shared/SectionHeading";
import Mandala from "../components/decor/Mandala";
import { INSTITUTIONS } from "../data/institutions";
import { IMG } from "../data/site";
import { School, ArrowRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const Institutions = () => {
  const { t, lang } = useLang();
  const pick = (o, k) => (lang === "kn" && o[`${k}Kn`]) || o[k];
  return (
    <PageLayout>
      <PageHero
        eyebrow={t("institutions.hero.eyebrow")}
        title={t("institutions.hero.title")}
        subtitle={t("institutions.hero.subtitle")}
        image={IMG.students}
      />

      {/* Philosophy infographic */}
      <section className="py-16" data-testid="edu-philosophy">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 text-center">
          <SectionHeading eyebrow={lang === "kn" ? "ಶೈಕ್ಷಣಿಕ ದರ್ಶನ" : "Educational Philosophy"} title={lang === "kn" ? "ಎರಡು ಪ್ರವಾಹಗಳು - ಒಂದೇ ದೃಷ್ಟಿ" : "Two streams - one vision"} align="center" />
          <div className="mt-14 grid md:grid-cols-3 gap-6 items-stretch">
            {[
              { title: "Traditional Learning", titleKn: "ಸಾಂಪ್ರದಾಯಿಕ ಶಿಕ್ಷಣ", desc: "Vedic recitation, Sanskrit grammar, Nyaya, Mimamsa and Vedanta - taught in the classical Gurukula system.", descKn: "ವೈದಿಕ ಪಠಣ, ಸಂಸ್ಕೃತ ವ್ಯಾಕರಣ, ನ್ಯಾಯ, ಮೀಮಾಂಸೆ ಹಾಗೂ ವೇದಾಂತ - ಶಾಸ್ತ್ರೀಯ ಗುರುಕುಲ ವ್ಯವಸ್ಥೆಯಲ್ಲಿ ಬೋಧನೆ." },
              { title: "Modern Education", titleKn: "ಆಧುನಿಕ ಶಿಕ್ಷಣ", desc: "Contemporary schools offering CBSE and state curricula with strong ethical foundations.", descKn: "ಶಕ್ತಿಯುತ ನೈತಿಕ ತಳಹದಿಯೊಂದಿಗೆ ಸಿಬಿಎಸ್‌ಇ ಮತ್ತು ರಾಜ್ಯ ಪಠ್ಯಕ್ರಮಗಳನ್ನು ನೀಡುವ ಸಮಕಾಲೀನ ಶಾಲೆಗಳು." },
              { title: "Holistic Development", titleKn: "ಸಮಗ್ರ ಬೆಳವಣಿಗೆ", desc: "Both streams converge to produce students who are grounded in tradition and equipped for the modern world.", descKn: "ಎರಡೂ ಪ್ರವಾಹಗಳು ಒಗ್ಗೂಡಿ ಪರಂಪರೆಯಲ್ಲಿ ನೆಲೆಗೊಂಡ, ಆಧುನಿಕ ಜಗತ್ತಿಗೆ ಸನ್ನದ್ಧರಾದ ವಿದ್ಯಾರ್ಥಿಗಳನ್ನು ರೂಪಿಸುತ್ತವೆ." },
            ].map((c) => (
              <div key={c.title} className="bg-[#FBF7F0] border border-[#D4AF37]/30 rounded-2xl p-8">
                <h3 className="font-serif-display text-xl text-[#B65A14]">{pick(c, "title")}</h3>
                <div className="mt-3 gold-underline mx-auto" />
                <p className="mt-4 text-sm text-[#3B2A1A]/80 leading-relaxed">{pick(c, "desc")}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Institution grid */}
      <section className="bg-sand-texture py-16 relative overflow-hidden" data-testid="institution-grid">
        <div className="absolute -top-20 -right-20 opacity-30 animate-mandala-slow"><Mandala size={340} color="#B65A14" opacity={0.5} /></div>
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <SectionHeading eyebrow={t("institutions.grid.eyebrow")} title={t("institutions.grid.title")} align="center" />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {INSTITUTIONS.map((i) => (
              <div key={i.name} className="bg-[#FBF7F0] border border-[#D4AF37]/25 rounded-2xl p-7 card-lift" data-testid={`institution-${i.name.replace(/\W/g, "-").toLowerCase()}`}>
                <div className="w-12 h-12 rounded-xl bg-[#B65A14] text-[#D4AF37] flex items-center justify-center mb-5">
                  <School className="w-5 h-5" />
                </div>
                <div className="overline text-[#B65A14]">{pick(i, "type")}</div>
                <h3 className="mt-2 font-serif-display text-xl text-[#3B2A1A] leading-tight">{pick(i, "name")}</h3>
                <p className="mt-3 text-sm text-[#3B2A1A]/80 leading-relaxed">{pick(i, "desc")}</p>
                <div className="mt-5 flex items-center justify-between text-xs">
                  <span className="text-[#3B2A1A]/60">{pick(i, "location")}</span>
                  <a href={i.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#B65A14] font-semibold">
                    {t("common.learnMore")} <ArrowRight className="w-3 h-3" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

    </PageLayout>
  );
};

export default Institutions;
